import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { BookOpenText, LogOut, LayoutDashboard, Library } from "lucide-react";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const { lang, setLang, t } = useT();
  const isAdminRoute = pathname.startsWith("/admin");
  const [acReader, setAcReader] = useState(false);

  useEffect(() => {
    if (isAdminRoute) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ecomics/auth/me`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setAcReader(!!d.user))
      .catch(() => {});
  }, [isAdminRoute]);

  const linkCls = (href) =>
    `bb-link text-sm ${pathname === href ? "text-[var(--bb-terracotta)]" : ""}`;

  const dashPath =
    user?.role === "admin" ? "/admin" :
    user?.role === "school" ? "/school" :
    user?.role === "donor" ? "/donor" : "/login";

  return (
    <header className="bb-glass sticky top-0 z-40" data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to={(pathname === "/welcome" || pathname === "/") ? "/" : "/"} className="flex items-center gap-3" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-full bg-[var(--bb-terracotta)] text-white grid place-items-center shadow-sm">
            <BookOpenText size={18} strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-xl tracking-tight text-[var(--bb-forest)]">{t("brand")}</div>
            <div className="bb-overline text-[var(--bb-muted)] text-[9px]">{t("brand_sub")}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {pathname !== "/welcome" && pathname !== "/" && (
            <>
              <Link to="/schools" className={linkCls("/schools")} data-testid="nav-schools">{t("nav_schools")}</Link>
              <Link to="/about" className={linkCls("/about")} data-testid="nav-about">{t("nav_how")}</Link>
              <Link to="/impact" className={linkCls("/impact")} data-testid="nav-impact">{t("nav_impact")}</Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {acReader && !isAdminRoute && (
            <Link to="/ecomics/library" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[var(--bb-forest)] hover:text-[var(--bb-terracotta)] transition" data-testid="nav-my-ecomics">
              <Library size={14}/> My E-Comics
            </Link>
          )}
          {!isAdminRoute && (
          <div
            className="inline-flex items-center rounded-full border border-[var(--bb-border)] bg-white/70 overflow-hidden text-[11px] font-medium"
            data-testid="lang-toggle"
            role="group"
            aria-label="Language"
          >
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition ${lang === "en" ? "bg-[var(--bb-forest)] text-white" : "text-[var(--bb-muted)] hover:text-[var(--bb-ink)]"}`}
              data-testid="lang-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
            <button
              onClick={() => setLang("kn")}
              className={`px-3 py-1.5 transition ${lang === "kn" ? "bg-[var(--bb-forest)] text-white" : "text-[var(--bb-muted)] hover:text-[var(--bb-ink)]"}`}
              data-testid="lang-kn"
              aria-pressed={lang === "kn"}
            >
              ಕನ್ನಡ
            </button>
          </div>
          )}
          {user ? (
            <>
              <Link to={dashPath} className="bb-btn-secondary hidden sm:inline-flex items-center gap-2 !py-2 !px-4 text-sm" data-testid="nav-dashboard">
                <LayoutDashboard size={14}/> {t("nav_dashboard")}
              </Link>
              <button onClick={logout} className="text-sm flex items-center gap-1 text-[var(--bb-muted)] hover:text-[var(--bb-terracotta)]" data-testid="nav-logout">
                <LogOut size={14}/> {t("nav_signout")}
              </button>
            </>
          ) : (
            pathname !== "/welcome" && pathname !== "/" && (
              <>
                <Link to="/login" className="text-sm text-[var(--bb-forest)] hover:text-[var(--bb-terracotta)] hidden sm:inline" data-testid="nav-login">{t("nav_signin")}</Link>
                <Link to="/schools" className="bb-btn-primary !py-2 !px-5 text-sm" data-testid="nav-donate-cta">{t("nav_donate")}</Link>
              </>
            )
          )}
        </div>
      </div>
    </header>
  );
}
